import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2, PenLine, X } from 'lucide-react';
import { adminApi } from '../services/api';
import { annoncerErreur, annoncerSucces, BarreProgression, EtatErreur } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════
   SIGNER — L'APERÇU D'ABORD, TOUJOURS

       « Signer »  →  le document s'affiche, signature déjà incrustée,
                      tel qu'il partira
                   →  « Envoyer »  ←  c'est CE clic qui signe

   L'aperçu n'engage à rien : le serveur le produit sans rien
   enregistrer. Signer d'abord et montrer ensuite, ce serait faire signer
   les yeux fermés — et c'est précisément ce qu'un document signé ne peut
   pas se permettre.

   D'où la mécanique de cet écran : le PDF est chargé en blob et affiché
   dans un cadre, et le bouton d'envoi ne devient actif qu'une fois
   l'aperçu affiché. On ne peut pas envoyer ce qu'on n'a pas vu.
   ═══════════════════════════════════════════════════════════════════════ */

export default function SignerDocument({ genre, document: doc, bloc, libelleBloc,
                                         onSigne, onFerme }) {
  const [apercu, setApercu] = useState(null);
  // L'objet erreur de l'aperçu : son statut (403, 404…) choisit le message.
  const [erreur, setErreur] = useState(null);
  const [rendulmpossible, setRenduImpossible] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const reference = doc.numero || 'ce document';

  const charger = useCallback(async () => {
    setErreur(null); setRenduImpossible('');
    try {
      const blob = await adminApi.apercuSignature(genre, doc.id, bloc);
      setApercu(URL.createObjectURL(blob));
    } catch (e) {
      // 503 : le droit est là, c'est le moteur de rendu qui manque.
      // Le distinguer d'un refus évite de faire chercher une permission
      // qu'on a déjà.
      if (e.statut === 503) setRenduImpossible(e.message);
      else setErreur(e);
    }
  }, [genre, doc.id, bloc]);

  useEffect(() => { charger(); }, [charger]);

  // Un blob object URL non révoqué retient le PDF en mémoire tant que
  // l'onglet vit.
  useEffect(() => () => { if (apercu) URL.revokeObjectURL(apercu); }, [apercu]);

  const envoyer = async () => {
    setEnvoi(true);
    try {
      const maj = await adminApi.signerDocument(genre, doc.id, bloc);
      annoncerSucces(`${doc.numero ? `${doc.numero} signé` : 'Document signé'} : votre signature est apposée.`);
      onSigne?.(maj);
    } catch (e) {
      annoncerErreur(e.message || 'La signature n’a pas pu être apposée.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-card)] rounded-carte w-full max-w-3xl shadow-carte
                      max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]
                        shrink-0 flex-wrap gap-3">
          <div>
            <h3 className="text-xl font-semibold text-[var(--text-bold)]">
              Signer {reference}
            </h3>
            <p className="text-[12.5px] text-[var(--text-muted)] mt-1">
              {libelleBloc} — relisez le document avant d’envoyer.
            </p>
          </div>
          <button onClick={onFerme} className="act act-icone act-tertiaire"
                  aria-label="Fermer">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {erreur ? (
            <EtatErreur erreur={erreur} onReessayer={charger} compact />
          ) : rendulmpossible ? (
            <div className="encart encart-attention">
              <AlertTriangle className="w-4 h-4" aria-hidden="true" />
              <p>{rendulmpossible}</p>
            </div>
          ) : apercu ? (
            <>
              <div className="encart encart-info">
                <p>
                  Voici le document <strong>tel qu’il partira</strong>, votre
                  signature déjà incrustée. Rien n’est enregistré tant que
                  vous n’avez pas cliqué sur « Envoyer ».
                </p>
              </div>
              <iframe
                title={`Aperçu signé de ${reference}`}
                src={apercu}
                className="w-full rounded-pastille border border-[var(--border)]"
                style={{ height: '58vh', minHeight: 320 }}
              />
            </>
          ) : (
            <div className="py-16 px-4 max-w-sm mx-auto">
              <BarreProgression libelle="Composition de l’aperçu…" />
            </div>
          )}
        </div>

        <div className="flex gap-3 p-6 border-t border-[var(--border)] shrink-0">
          <button type="button" onClick={onFerme} className="act act-contour flex-1">
            Annuler
          </button>
          {/* Inactif tant que l'aperçu n'est pas là : on ne signe pas ce
              qu'on n'a pas vu. */}
          <button type="button" onClick={envoyer} disabled={!apercu || envoi}
                  aria-busy={envoi || undefined} className="act flex-1">
            {envoi ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Check className="w-4 h-4" />}
            Envoyer — je signe
          </button>
        </div>
      </div>
    </div>
  );
}

/* Le bouton qui ouvre l'aperçu, avec le garde-fou d'usage : sans
   signature enregistrée, on renvoie vers le profil plutôt que d'échouer
   à l'envoi. */
export function BoutonSigner({ genre, document: doc, bloc, libelleBloc,
                              signatureEnregistree, onSigne, className = 'act' }) {
  const [ouvert, setOuvert] = useState(false);

  if (!signatureEnregistree) {
    return (
      <span className="text-[12px] text-[var(--text-muted)]">
        Enregistrez votre signature dans <strong>Mon profil</strong> pour
        signer ici.
      </span>
    );
  }

  return (
    <>
      <button type="button" onClick={() => setOuvert(true)} className={className}>
        <PenLine className="w-4 h-4" />
        Signer
      </button>
      {ouvert && (
        <SignerDocument
          genre={genre} document={doc} bloc={bloc} libelleBloc={libelleBloc}
          onFerme={() => setOuvert(false)}
          onSigne={(maj) => { setOuvert(false); onSigne?.(maj); }}
        />
      )}
    </>
  );
}
